import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEO, COMPANY, SITE_URL, NAV } from "@/data/site";

const upsert = (selector, attr, key, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};
const setName = (name, value) => upsert(`meta[name="${name}"]`, "name", name, value);
const setProp = (prop, value) => upsert(`meta[property="${prop}"]`, "property", prop, value);

const pageName = (pathname) => {
  const nav = NAV.find((n) => n.to === pathname);
  if (nav) return nav.label;
  const meta = SEO[pathname];
  return meta ? meta.title.split("|")[0].trim() : "";
};

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = SEO[pathname] || SEO["/"];
    const url = SITE_URL + (pathname === "/" ? "/" : pathname);
    const ogImage = `${SITE_URL}/og-image.jpg`;

    document.title = meta.title;
    setName("description", meta.description);
    setName("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph + Twitter
    setProp("og:title", meta.title);
    setProp("og:description", meta.description);
    setProp("og:url", url);
    setProp("og:image", ogImage);
    setProp("og:type", "website");
    setProp("og:site_name", COMPANY.name);
    setProp("og:locale", "en_GB");
    setName("twitter:card", "summary_large_image");
    setName("twitter:title", meta.title);
    setName("twitter:description", meta.description);
    setName("twitter:image", ogImage);

    // ---- Structured data (@graph) ----
    const org = {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Andrews Building Company Limited",
      legalName: "Andrews Building Company Limited",
      url: SITE_URL,
      logo: `${SITE_URL}/icon-512.png`,
      foundingDate: "1986",
      email: COMPANY.email,
      telephone: "+441908211262",
      taxID: "04982965",
      description: SEO["/"].description,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Alphabet House, Bleak Hall",
        addressLocality: "Milton Keynes",
        postalCode: "MK6 1NE",
        addressCountry: "GB",
      },
    };

    const localBusiness = {
      "@type": ["GeneralContractor", "LocalBusiness"],
      "@id": `${SITE_URL}/#localbusiness`,
      name: COMPANY.name,
      image: ogImage,
      url: SITE_URL,
      email: COMPANY.email,
      telephone: "+441908211262",
      priceRange: "££",
      foundingDate: "1986",
      parentOrganization: { "@id": `${SITE_URL}/#organization` },
      address: {
        "@type": "PostalAddress",
        streetAddress: "Alphabet House, Bleak Hall",
        addressLocality: "Milton Keynes",
        postalCode: "MK6 1NE",
        addressCountry: "GB",
      },
      geo: { "@type": "GeoCoordinates", latitude: 52.0197, longitude: -0.7644 },
      areaServed: {
        "@type": "GeoCircle",
        geoMidpoint: { "@type": "GeoCoordinates", latitude: 52.0406, longitude: -0.7594 },
        geoRadius: "128000",
        description: "Milton Keynes and South Central England",
      },
      knowsAbout: [
        "Industrial refurbishment",
        "Commercial refurbishment",
        "Dilapidations",
        "Roof replacement",
        "Over-cladding",
        "Groundworks",
        "Structural works",
        "Office fit-outs",
      ],
    };

    const website = {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: COMPANY.name,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en-GB",
    };

    const isHome = pathname === "/";
    const crumbs = [{ name: "Home", item: `${SITE_URL}/` }];
    if (!isHome) crumbs.push({ name: pageName(pathname), item: url });

    const breadcrumb = {
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.item,
      })),
    };

    const webpage = {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: meta.title,
      description: meta.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#localbusiness` },
      inLanguage: "en-GB",
      primaryImageOfPage: ogImage,
      breadcrumb: { "@id": `${url}#breadcrumb` },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", ".speakable"],
      },
    };

    const graph = [org, website, localBusiness, webpage, breadcrumb];

    if (pathname === "/services") {
      graph.push({
        "@type": "OfferCatalog",
        "@id": `${url}#services`,
        name: "Building & refurbishment services",
        itemListElement: [
          "Industrial refurbishment",
          "Commercial refurbishment",
          "Dilapidations",
          "Specialist groundworks",
          "Specialist structural works",
          "Office fit-outs",
          "External decorating",
          "Internal decorating",
          "Roof replacement & refurbishment",
          "Over-cladding",
          "Fire door replacements",
          "Commercial gas works",
          "NIC/EIC electrical works",
          "Asbestos removal",
          "Signage supply & installation",
        ].map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s, provider: { "@id": `${SITE_URL}/#localbusiness` } },
        })),
      });
    }

    let ld = document.getElementById("ld-json");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "ld-json";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  }, [pathname]);

  return null;
}
